import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DatePicker from 'react-datepicker';
import { logPolicyAction } from '../utils/policyLogger';
import PolicyDocumentsTab from '../components/PolicyDocumentsTab';
import { usePermissions } from '../context/PermissionsContext';

interface Policy {
  id: number;
  policy_number: string;
  product_name: string;
  insurer: string;
  status: 'active' | 'cancelled';
  start_date: string;
  end_date?: string;
  renewal_date?: string;
  premium_amount: number;
  premium_collection_date?: string;
  whatsapp_notification?: boolean;
  email_notification?: boolean; 
  cancellation_date?: string;
  cancellation_reason?: string;
  client_id: number;
  quote_id?: number;
}

const PolicyDetail = () => {
  const { can } = usePermissions();
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('policyDetails');
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [user, setUser] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [policyNotes, setPolicyNotes] = useState<any[]>([]);
  const [cancelData, setCancelData] = useState({
    termination_date: new Date(),
    cancellation_reason: ''
  });
  const [premiumCollectionDay, setPremiumCollectionDay] = useState<string>('');
  const [clientProducts, setClientProducts] = useState<any[]>([]);
  const [quoteReference, setQuoteReference] = useState<string>('');
  const [productsLoading, setProductsLoading] = useState<boolean>(false);
  const [isAddProductModalVisible, setIsAddProductModalVisible] = useState(false);
  const [showRemoveProductModal, setShowRemoveProductModal] = useState(false);
  const [productToRemove, setProductToRemove] = useState<any>(null);
  const [removalReason, setRemovalReason] = useState('');
  const [removingProduct, setRemovingProduct] = useState(false);
  const [cancelledProducts, setCancelledProducts] = useState<any[]>([]);
  // Sort state for each product type
  const [propertySortOrder, setPropertySortOrder] = useState<'asc' | 'desc'>('desc');
  const [householdSortOrder, setHouseholdSortOrder] = useState<'asc' | 'desc'>('desc');
  const [vehicleSortOrder, setVehicleSortOrder] = useState<'asc' | 'desc'>('desc');
  const [trailerSortOrder, setTrailerSortOrder] = useState<'asc' | 'desc'>('desc');
  const [watercraftSortOrder, setWatercraftSortOrder] = useState<'asc' | 'desc'>('desc');
  const [riskItemSortOrder, setRiskItemSortOrder] = useState<'asc' | 'desc'>('desc');
  const [valueAddedSortOrder, setValueAddedSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortedProducts, setSortedProducts] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    policy_number: '',
    insurer: '',
    status: 'active' as 'active' | 'cancelled',
    start_date: null as Date | null,
    end_date: null as Date | null,
    renewal_date: null as Date | null,
    premium_amount: '',
     premium_collection_date: null as Date | null,
    whatsapp_notification: false,
    email_notification: false,
    cancellation_date: null as Date | null,
    cancellation_reason: ''
  });

  useEffect(() => {
    fetchData();
  }, [policyId]);

  const fetchData = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Get user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Fetch policy details
    const policyRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/policies/${policyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const policyData = policyRes.data.data;
    
    console.log('🔍 Policy data from API:', policyData);
    console.log('🔍 quote_id in policy:', policyData.quote_id);
    
    setPolicy(policyData);
    await fetchPolicyNotes();

    // Fetch client details
    if (policyData.client_id) {
      const clientRes = await axios.get(`${process.env.REACT_APP_API_URL}/clients/${policyData.client_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(clientRes.data);
    }

    // Initialize form data
    setFormData({
      policy_number: policyData.policy_number,
      insurer: policyData.insurer || '',
      status: policyData.status,
      start_date: policyData.start_date ? new Date(policyData.start_date) : null,
      end_date: policyData.end_date ? new Date(policyData.end_date) : null,
      renewal_date: policyData.renewal_date ? new Date(policyData.renewal_date) : null,
      premium_amount: policyData.premium_amount.toString(),
      premium_collection_date: policyData.premium_collection_date ? new Date(policyData.premium_collection_date) : null,
      whatsapp_notification: policyData.whatsapp_notification || false,
      email_notification: policyData.email_notification || false,
      cancellation_date: policyData.cancellation_date ? new Date(policyData.cancellation_date) : null,
      cancellation_reason: policyData.cancellation_reason || ''
    });

    if (policyData.premium_collection_date) {
      const dateStr = policyData.premium_collection_date;
      const day = parseInt(dateStr.split('-')[2]);
      setPremiumCollectionDay(day.toString());
    } else {
      setPremiumCollectionDay('');
    }

    // FETCH PRODUCTS - Use the new policy-products endpoint
    let productsFetched = false;

    if (policyData.quote_id && policyData.quote_id > 0) {
      console.log('✅ Found quote_id, fetching products for policy:', policyId);
      
      try {
        // Fetch cancelled products
        const cancelledResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/policies/${policyId}/cancelled-products`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const cancelledList = cancelledResponse.data.data || [];
        setCancelledProducts(cancelledList);
        
        // Fetch all products linked to this policy using the new endpoint
        const productsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/policy-products/${policyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        let products = productsResponse.data.data || [];
        console.log(`📊 Found ${products.length} products for policy ${policyId}`);
        
        // Filter out cancelled products
        const cancelledKeys = new Set(
          cancelledList.map((p: any) => `${p.product_type}-${p.product_id}`)
        );
        
        const activeProducts = products.filter((product: any) => {
          const key = `${product.product_type}-${product.id}`;
          return !cancelledKeys.has(key);
        });
        
        console.log(`📊 Active products after filtering: ${activeProducts.length}`);
        
        // Get quote reference for display
        let quoteRef = '';
        try {
          const quoteRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${policyData.quote_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          quoteRef = quoteRes.data?.data?.referenceNumber || quoteRes.data?.data?.reference || `Q-${policyData.quote_id}`;
        } catch (err) {
          quoteRef = `Q-${policyData.quote_id}`;
        }
        
        setQuoteReference(quoteRef);
        setClientProducts(activeProducts);
        setSortedProducts(sortProductsByDate(activeProducts, sortOrder));
        productsFetched = true;
        
      } catch (err) {
        console.error('Failed to fetch products for policy:', err);
        setClientProducts([]);
        setProductsLoading(false);
      }
    } else {
  console.log('⚠️ No quote_id found, checking localStorage for products by policy_id');
  
  // Check localStorage for value-added products
  const vaStorageKey = `policy_${policyId}_value_added`;
  const storedVaProducts = JSON.parse(localStorage.getItem(vaStorageKey) || '[]');
  
  // ✅ Check localStorage for risk items
  const riskStorageKey = `policy_${policyId}_risk_item`;
  const storedRiskItems = JSON.parse(localStorage.getItem(riskStorageKey) || '[]');
  
  // Check localStorage for other product types (for future compatibility)
  const propertyStorageKey = `policy_${policyId}_property`;
  const storedProperties = JSON.parse(localStorage.getItem(propertyStorageKey) || '[]');
  
  const vehicleStorageKey = `policy_${policyId}_vehicle`;
  const storedVehicles = JSON.parse(localStorage.getItem(vehicleStorageKey) || '[]');
  
  const householdStorageKey = `policy_${policyId}_household`;
  const storedHousehold = JSON.parse(localStorage.getItem(householdStorageKey) || '[]');
  
  const watercraftStorageKey = `policy_${policyId}_watercraft`;
  const storedWatercraft = JSON.parse(localStorage.getItem(watercraftStorageKey) || '[]');
  
  const trailerStorageKey = `policy_${policyId}_trailer`;
  const storedTrailers = JSON.parse(localStorage.getItem(trailerStorageKey) || '[]');
  
  // Combine all localStorage products
  let allLocalProducts: any[] = [];
  
  if (storedVaProducts.length > 0) {
    console.log(`📋 Found ${storedVaProducts.length} value-added products in localStorage for policy ${policyId}`);
    allLocalProducts = allLocalProducts.concat(
      storedVaProducts.map((item: any) => ({ 
        ...item, 
        product_type: 'value-added',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date
      }))
    );
  }
  
  if (storedRiskItems.length > 0) {
    console.log(`📋 Found ${storedRiskItems.length} risk items in localStorage for policy ${policyId}`);
    // ✅ Log the risk items to debug
    console.log('📋 Risk items data:', storedRiskItems);
    allLocalProducts = allLocalProducts.concat(
      storedRiskItems.map((item: any) => ({ 
        ...item, 
        product_type: 'risk-items',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date,
        // Ensure required fields for display
        item_name: item.item_name || item.description || 'Risk Item'
      }))
    );
  }
  
  if (storedProperties.length > 0) {
    allLocalProducts = allLocalProducts.concat(
      storedProperties.map((item: any) => ({ 
        ...item, 
        product_type: 'properties',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date
      }))
    );
  }
  
  if (storedVehicles.length > 0) {
    allLocalProducts = allLocalProducts.concat(
      storedVehicles.map((item: any) => ({ 
        ...item, 
        product_type: 'vehicles',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date
      }))
    );
  }
  
  if (storedHousehold.length > 0) {
    allLocalProducts = allLocalProducts.concat(
      storedHousehold.map((item: any) => ({ 
        ...item, 
        product_type: 'household-contents',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date
      }))
    );
  }
  
  if (storedWatercraft.length > 0) {
    allLocalProducts = allLocalProducts.concat(
      storedWatercraft.map((item: any) => ({ 
        ...item, 
        product_type: 'water-craft',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date
      }))
    );
  }
  
  if (storedTrailers.length > 0) {
    allLocalProducts = allLocalProducts.concat(
      storedTrailers.map((item: any) => ({ 
        ...item, 
        product_type: 'trailer-caravans',
        status: 'active',
        inception_date: item.inception_date || policyData.start_date
      }))
    );
  }
  
  // Remove duplicates by id
  const seen = new Set();
  const uniqueProducts = allLocalProducts.filter((product) => {
    const key = product.id || product.uniqueKey || `${product.product_type}-${Date.now()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  
  if (uniqueProducts.length > 0) {
    console.log(`📋 Found ${uniqueProducts.length} total products in localStorage for policy ${policyId}`);
    console.log('📊 Product types:', uniqueProducts.map((p: any) => ({ 
      type: p.product_type, 
      id: p.id, 
      name: p.item_name || p.product_name || p.description 
    })));
    setClientProducts(uniqueProducts);
    productsFetched = true;
  } else {
    console.log('❌ No products found in localStorage for policy:', policyId);
    setClientProducts([]);
  }
}

    if (!productsFetched) {
      console.log('❌ No products found for this policy');
      setClientProducts([]);
    }

  } catch (err) {
    console.error('Failed to fetch policy:', err);
    setError('Failed to load policy details');
  } finally {
    setLoading(false);
  }
};

  const fetchPolicyNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const notesRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/policy-notes/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicyNotes(notesRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch policy notes:', err);
    }
  };

  const fetchClientProducts = async (clientId: number, quoteId: number | null, cancelledProductsList: any[] = []) => {
  // This function is now replaced by the new policy-products endpoint
  // Keeping for backward compatibility but it should not be called
  console.log('⚠️ fetchClientProducts called - use policy-products endpoint instead');
  setClientProducts([]);
  setProductsLoading(false);
};

  const fetchCancelledProducts = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/policies/${policyId}/cancelled-products`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const cancelledList = response.data.data || [];
    console.log('🔍 CANCELLED PRODUCTS FROM API:', cancelledList);
    console.log('🔍 CANCELLED PRODUCTS DETAILS:', cancelledList.map((p: any) => ({
      product_type: p.product_type,
      product_id: p.product_id,
      product_name: p.product_name
    })));
    
    setCancelledProducts(response.data.data || []);
    console.log(`📋 Found ${response.data.data?.length || 0} cancelled products`);
  } catch (err) {
    console.error('Failed to fetch cancelled products:', err);
  }
};

// Sort products by inception date for a specific product type
const sortProductsByDate = (products: any[], order: 'asc' | 'desc') => {
  return [...products].sort((a, b) => {
    const dateA = a.inception_date || a.created_at || policy?.start_date;
    const dateB = b.inception_date || b.created_at || policy?.start_date;
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();
    
    return order === 'asc' ? timeA - timeB : timeB - timeA;
  });
};

const toggleSort = (type: string) => {
  switch(type) {
    case 'properties':
      setPropertySortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
    case 'household-contents':
      setHouseholdSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
    case 'vehicles':
      setVehicleSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
    case 'trailer-caravans':
      setTrailerSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
    case 'water-craft':
      setWatercraftSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
    case 'risk-items':
      setRiskItemSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
    case 'value-added':
      setValueAddedSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
      break;
  }
};

const handleSortByDate = () => {
  const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
  setSortOrder(newOrder);
  
  const sorted = sortProductsByDate(clientProducts.filter((p: any) => p.status !== 'cancelled'), newOrder);
  setSortedProducts(sorted);
};

// Helper functions for product display
const getProductTypeDisplay = (type: string): string => {
  const typeMap: { [key: string]: string } = {
    'properties': 'Property',
    'property': 'Property',
    'vehicles': 'Vehicle',
    'vehicle': 'Vehicle',
    'household-contents': 'Household Contents',
    'household': 'Household Contents',
    'risk-items': 'Risk Item',
    'risk-item': 'Risk Item',
    'risk_item': 'Risk Item',
    'water-craft': 'Water Craft',
    'watercraft': 'Water Craft',
    'trailer-caravans': 'Trailer/Caravan',
    'trailer': 'Trailer/Caravan',
    'value-added': 'Value Added',
    'value_added': 'Value Added'
  };
  return typeMap[type] || type;
};

const getProductName = (product: any, type: string): string => {
  // Normalize the type
  const normalizedType = type === 'risk-item' || type === 'risk_item' ? 'risk-items' : type;
  
  switch (normalizedType) {
    case 'properties':
      return product.property_type || product.address_line_1 || 'Property';
    case 'vehicles':
      return `${product.make || ''} ${product.model || ''}`.trim() || 'Vehicle';
    case 'household-contents':
      return 'Household Contents';
    case 'risk-items':
      return product.item_name || product.itemName || product.description || 'Risk Item';
    case 'water-craft':
      return product.vessel_name || product.vesselName || 'Water Craft';
    case 'trailer-caravans':
      return product.type || 'Trailer/Caravan';
    case 'value-added':
      return product.product_type || product.name || 'Value Added';
    default:
      return 'Product';
  }
};

const getProductValue = (product: any): number => {
  return product.insured_value || product.insuredValue || product.value || 0;
};

const getPropertyAddress = (product: any): string => {
  // Try different possible field names for address
  const address = product.address_line_1 || product.addressLine1 || '';
  const suburb = product.suburb || '';
  const city = product.city || product.city_town || '';
  
  const parts = [];
  if (address) parts.push(address);
  if (suburb) parts.push(suburb);
  if (city) parts.push(city);
  
  return parts.length > 0 ? parts.join(', ') : '—';
};

const getProductDescription = (product: any, type: string): string => {
  switch (type) {
    case 'properties':
      return product.property_type || product.address_line_1 || 'Property';
    case 'vehicles':
      return `${product.make || ''} ${product.model || ''} ${product.year || ''}`.trim() || 'Vehicle';
    case 'household-contents':
      return product.description || 'Household Contents';
    case 'risk-items':
      return product.item_name || product.name || product.description || 'Risk Item';
    case 'water-craft':
      return product.vessel_name || product.name || `${product.make || ''} ${product.model || ''}`.trim() || 'Water Craft';
    case 'trailer-caravans':
      return `${product.make || ''} ${product.model || ''}`.trim() || product.vehicle_type || 'Trailer/Caravan';
    case 'value-added':
      return product.description || product.name || 'Value Added';
    default:
      return product.description || product.name || 'Product';
  }
};

// Get edit URL for a product
const getProductEditUrl = (product: any): string => {
  const currentQuoteId = policy?.quote_id;
  const clientIdValue = policy?.client_id;
  const returnUrl = `/policy/${policyId}`;

  // Normalize the product type
  let productType = product.product_type;
  if (productType === 'risk-item' || productType === 'risk_item') {
    productType = 'risk-items';
  }
  
  switch (productType) {
  case 'properties':
    return `/add-property/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  case 'vehicles':
    return `/add-vehicle/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  case 'household-contents':
    return `/add-household-contents/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  case 'risk-items':
    return `/add-risk-item/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  case 'water-craft':
    return `/add-water-craft/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  case 'trailer-caravans':
    return `/add-trailer-caravan/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  case 'value-added':
    return `/add-value-added/${currentQuoteId}?clientId=${clientIdValue}&editId=${product.id}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`;
  default:
    return '#';
}
};

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDateChange = (date: Date | null, field: string) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  const handleUpdatePolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const terminationDate = cancelData.termination_date ? cancelData.termination_date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

      // Calculate premium_collection_date based on selected day
      let premiumCollectionDateValue = null;
      if (premiumCollectionDay === 'last-day') {
        const today = new Date();
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        // Format as YYYY-MM-DD without timezone conversion
        const year = lastDay.getFullYear();
        const month = String(lastDay.getMonth() + 1).padStart(2, '0');
        const day = String(lastDay.getDate()).padStart(2, '0');
        premiumCollectionDateValue = `${year}-${month}-${day}`;
      } else if (premiumCollectionDay) {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(parseInt(premiumCollectionDay)).padStart(2, '0');
        premiumCollectionDateValue = `${year}-${month}-${day}`;
      }

      const payload = {
        clientId: policy?.client_id,
        policy_number: formData.policy_number,
        product_name: policy?.product_name || 'Policy',
        insurer: formData.insurer,
        status: formData.status,
        start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
        end_date: terminationDate,
        renewal_date: formData.renewal_date ? formData.renewal_date.toISOString().split('T')[0] : null,
        premium_amount: parseFloat(formData.premium_amount),
        premium_collection_date: formData.premium_collection_date ? formData.premium_collection_date.toISOString().split('T')[0] : null,
        whatsapp_notification: formData.whatsapp_notification,
        email_notification: formData.email_notification,
        cancellation_date: terminationDate,  // Also set cancellation date
        cancellation_reason: cancelData.cancellation_reason || 'Policy cancelled by user'
      };

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/policies/${policyId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh policy data from server
      const policyRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/policies/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedPolicy = policyRes.data.data;
      setPolicy(updatedPolicy);
      
      // Update formData with the refreshed data
      setFormData({
        policy_number: updatedPolicy.policy_number,
        insurer: updatedPolicy.insurer || '',
        status: updatedPolicy.status,
        start_date: updatedPolicy.start_date ? new Date(updatedPolicy.start_date) : null,
        renewal_date: updatedPolicy.renewal_date ? new Date(updatedPolicy.renewal_date) : null,
        end_date: updatedPolicy.end_date ? new Date(updatedPolicy.end_date) : null,
        premium_amount: updatedPolicy.premium_amount.toString(),
        premium_collection_date: updatedPolicy.premium_collection_date ? new Date(updatedPolicy.premium_collection_date) : null,
        whatsapp_notification: updatedPolicy.whatsapp_notification || false,
        email_notification: updatedPolicy.email_notification || false,
        cancellation_date: updatedPolicy.cancellation_date ? new Date(updatedPolicy.cancellation_date) : null,
        cancellation_reason: updatedPolicy.cancellation_reason || ''
      });

      // Log the edit action
      const changes = {
        before: {
          policy_number: policy?.policy_number,
          product_name: policy?.product_name,
          insurer: policy?.insurer,
          status: policy?.status,
          premium_amount: policy?.premium_amount,
          premium_collection_date: policy?.premium_collection_date,
          whatsapp_notification: policy?.whatsapp_notification,
          email_notification: policy?.email_notification
        },
        after: {
          policy_number: formData.policy_number,
          insurer: formData.insurer,
          status: formData.status,
          premium_amount: formData.premium_amount,
          premium_collection_date: formData.premium_collection_date?.toISOString().split('T')[0],
          whatsapp_notification: formData.whatsapp_notification,
          email_notification: formData.email_notification
        }
      };

      await logPolicyAction(
        parseInt(policyId!),
        'EDITED',
        `Policy updated by user`,
        changes
      );

      setIsEditing(false);
      
    } catch (err) {
      console.error('Failed to update policy:', err);
      setError('Failed to update policy');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPolicy = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const terminationDate = cancelData.termination_date 
        ? cancelData.termination_date.toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0];

      const payload = {
        clientId: policy?.client_id,
        policy_number: formData.policy_number,
        product_name: policy?.product_name || 'Policy',
        insurer: formData.insurer,
        status: 'cancelled',
        start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
        end_date: terminationDate,
        renewal_date: formData.renewal_date ? formData.renewal_date.toISOString().split('T')[0] : null,
        premium_amount: parseFloat(formData.premium_amount),
        premium_collection_date: formData.premium_collection_date ? formData.premium_collection_date.toISOString().split('T')[0] : null,
        whatsapp_notification: formData.whatsapp_notification,
        email_notification: formData.email_notification,
        cancellation_date: terminationDate,  // CHANGE THIS LINE
        cancellation_reason: cancelData.cancellation_reason || 'Policy cancelled by user'
      };

    await axios.put(
      `${process.env.REACT_APP_API_URL}/api/policies/${policyId}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

      // Refresh policy data from server
      const policyRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/policies/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedPolicy = policyRes.data.data;
      setPolicy(updatedPolicy);
      
      // Update formData with the refreshed data
      setFormData({
        policy_number: updatedPolicy.policy_number,
        insurer: updatedPolicy.insurer || '',
        status: updatedPolicy.status,
        start_date: updatedPolicy.start_date ? new Date(updatedPolicy.start_date) : null,
        end_date: updatedPolicy.end_date ? new Date(updatedPolicy.end_date) : null,
        renewal_date: updatedPolicy.renewal_date ? new Date(updatedPolicy.renewal_date) : null,
        premium_amount: updatedPolicy.premium_amount.toString(),
        premium_collection_date: updatedPolicy.premium_collection_date ? new Date(updatedPolicy.premium_collection_date) : null,
        whatsapp_notification: updatedPolicy.whatsapp_notification || false,
        email_notification: updatedPolicy.email_notification || false,
        cancellation_date: updatedPolicy.cancellation_date ? new Date(updatedPolicy.cancellation_date) : null,
        cancellation_reason: updatedPolicy.cancellation_reason || ''
      });

      // Log the cancellation action
      await logPolicyAction(
        parseInt(policyId!),
        'CANCELLED',
        `Policy cancelled with termination date: ${terminationDate}. Reason: ${cancelData.cancellation_reason || 'No reason provided'}`,
        { termination_date: terminationDate, cancellation_reason: cancelData.cancellation_reason }
      );

      setShowCancelModal(false);
      setCancelData({
        termination_date: new Date(),
        cancellation_reason: ''
      });

    } catch (err) {
      console.error('Failed to cancel policy:', err);
      setError('Failed to cancel policy');
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Helper functions for Add Product modal navigation
const handleOpenPreviousInsuranceModal = () => {
  const currentQuoteId = policy?.quote_id;
  const clientIdValue = policy?.client_id;
  
  if (!currentQuoteId) {
    alert('No quote associated with this policy');
    return;
  }
  
  const returnUrl = `/policy/${policyId}`;
  navigate(`/add-previous-insurance/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
};

const handleOpenClaimModal = () => {
  const currentQuoteId = policy?.quote_id;
  const clientIdValue = policy?.client_id;
  
  if (!currentQuoteId) {
    alert('No quote associated with this policy');
    return;
  }
  
  const returnUrl = `/policy/${policyId}`;
  navigate(`/add-previous-claim/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
};

// Handle product removal
const handleRemoveProduct = (product: any) => {
  setProductToRemove(product);
  setRemovalReason('');
  setShowRemoveProductModal(true);
};

const confirmRemoveProduct = async () => {
  if (!productToRemove || !removalReason.trim()) {
    alert('Please provide a reason for removal');
    return;
  }

  setRemovingProduct(true);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication required');
      return;
    }

    // Check if this is a localStorage product (has isTemp flag or no database ID)
    const isLocalStorageProduct = productToRemove.isTemp || 
                                  (typeof productToRemove.id === 'string' && productToRemove.id.startsWith('temp-'));

    if (isLocalStorageProduct) {
      // Remove from localStorage
      const storageKey = `policy_${policyId}_value_added`;
      const storedProducts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updatedProducts = storedProducts.filter((item: any) => item.id !== productToRemove.id);
      localStorage.setItem(storageKey, JSON.stringify(updatedProducts));
      
      // Update state
      setClientProducts(prev => prev.filter((p: any) => p.id !== productToRemove.id));
      
      // Add to cancelled products in localStorage
      const cancelledKey = `policy_${policyId}_cancelled`;
      const cancelledItems = JSON.parse(localStorage.getItem(cancelledKey) || '[]');
      cancelledItems.push({
        ...productToRemove,
        cancellation_date: new Date().toISOString().split('T')[0],
        cancellation_reason: removalReason
      });
      localStorage.setItem(cancelledKey, JSON.stringify(cancelledItems));
      
      setShowRemoveProductModal(false);
      setProductToRemove(null);
      setRemovalReason('');
      alert('Product has been removed successfully');
      setRemovingProduct(false);
      return;
    }

    // Regular database product - call API to cancel
    const productName = getProductName(productToRemove, productToRemove.product_type);
    const productDescription = getProductDescription(productToRemove, productToRemove.product_type);
    const productValue = getProductValue(productToRemove);

    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/policies/${policyId}/cancel-product`,
      {
        quote_id: policy?.quote_id,
        client_id: policy?.client_id,
        product_type: productToRemove.product_type,
        product_id: productToRemove.id,
        product_name: productName,
        product_description: productDescription,
        product_value: productValue,
        cancellation_reason: removalReason
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Refresh cancelled products
    const cancelledResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/policies/${policyId}/cancelled-products`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const cancelledList = cancelledResponse.data.data || [];
    setCancelledProducts(cancelledList);

    // Refresh active products using the new endpoint
    if (policy?.quote_id) {
      const productsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/policy-products/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let products = productsResponse.data.data || [];
      const cancelledKeys = new Set(
        cancelledList.map((p: any) => `${p.product_type}-${p.product_id}`)
      );
      
      const activeProducts = products.filter((product: any) => {
        const key = `${product.product_type}-${product.id}`;
        return !cancelledKeys.has(key);
      });
      
      setClientProducts(activeProducts);
      setSortedProducts(sortProductsByDate(activeProducts, sortOrder));
    }

    setShowRemoveProductModal(false);
    setProductToRemove(null);
    setRemovalReason('');
    alert('Product has been cancelled successfully');
    
  } catch (error: any) {
    console.error('Failed to cancel product:', error);
    alert(error.response?.data?.message || 'Failed to cancel product. Please try again.');
  } finally {
    setRemovingProduct(false);
  }
};

const handleOpenNoteModal = () => {
  const currentQuoteId = policy?.quote_id;
  const clientIdValue = policy?.client_id;
  
  if (!currentQuoteId) {
    alert('No quote associated with this policy');
    return;
  }
  
  const returnUrl = `/policy/${policyId}`;
  navigate(`/add-note/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
};

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

    if (!can('policies', 'read')) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to view Policies.</p>
      </div>
    );
  }


  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Loading...</div>;
  }

  if (error || !policy) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Policy not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-800"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={`Policy: ${policy.policy_number}`}
          user={user}
          client={client}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={!!client}
        />
        <div className="flex-1 p-6">
          <div className="bg-white rounded-lg shadow-md p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Policy Management</h2>
              <button
                onClick={() => navigate(`/personal/${policy.client_id}`, { state: { activeTab: 'policies' } })}
                className="text-gray-600 hover:text-gray-800"
              >
                Back to Client
              </button>
            </div>

            {/* Tabs */}
<div className="flex space-x-2 mb-6 border-b border-gray-200 overflow-x-auto">
  {['policyDetails', 'documents', 'notes', 'whatsapp', 'email'].map((tab) => (
    <button
      key={tab}
      className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
        activeTab === tab 
          ? 'border-b-2 border-blue-500 text-blue-500' 
          : 'text-gray-500 hover:text-blue-500'
      }`}
      onClick={() => setActiveTab(tab)}
    >
      {tab === 'policyDetails' ? 'Policy Details' : 
       tab === 'whatsapp' ? 'WhatsApp' : 
       tab.charAt(0).toUpperCase() + tab.slice(1)}
    </button>
  ))}
</div>

            {/* Action Buttons */}
              <div className="mb-6 flex justify-end gap-3">
                {!isEditing && policy.status === 'active' && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Cancel Policy
                  </button>
                )}

                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Edit Policy
                  </button>
                )}
                <button
                  onClick={() => setIsAddProductModalVisible(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  + Add New Product
                </button>
              </div>

            {/* Tab Content */}
            <div className="mt-4">
              {activeTab === 'policyDetails' && (
                <div>
                  {isEditing ? (
                    <form onSubmit={handleUpdatePolicy} className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Policy Number *
                          </label>
                          <input
                            type="text"
                            name="policy_number"
                            value={formData.policy_number}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Insurer
                          </label>
                          <input
                            type="text"
                            name="insurer"
                            value={formData.insurer}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Premium Collection Date
                          </label>
                          <div className="flex gap-3">
                            <select
                              value={premiumCollectionDay}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPremiumCollectionDay(value);
                                
                                if (value === 'last-day') {
                                  // Create a date that represents the last day of month
                                  const today = new Date();
                                  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                                  setFormData(prev => ({ ...prev, premium_collection_date: lastDay }));
                                } else if (value) {
                                  // Create date at noon UTC to avoid timezone issues
                                  const today = new Date();
                                  const selectedDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), parseInt(value), 12, 0, 0));
                                  setFormData(prev => ({ ...prev, premium_collection_date: selectedDate }));
                                } else {
                                  setFormData(prev => ({ ...prev, premium_collection_date: null }));
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Not set</option>
                              {[...Array(31)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                              ))}
                              <option value="last-day">Last Day of Month</option>
                            </select>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Select the day of the month for premium collection
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notification Methods
                          </label>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                name="whatsapp_notification"
                                checked={formData.whatsapp_notification}
                                onChange={handleInputChange}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">WhatsApp</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                name="email_notification"
                                checked={formData.email_notification}
                                onChange={handleInputChange}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Email</span>
                            </label>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status *
                          </label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="active">Active</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Premium Amount (ZAR) *
                          </label>
                          <input
                            type="number"
                            name="premium_amount"
                            value={formData.premium_amount}
                            onChange={handleInputChange}
                            required
                            min="0.01"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Inception Date
                          </label>
                          <DatePicker
                            selected={formData.start_date}
                            onChange={(date) => handleDateChange(date, 'start_date')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            dateFormat="yyyy-MM-dd"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Termination Date
                          </label>
                          <DatePicker
                            selected={formData.end_date}
                            onChange={(date) => handleDateChange(date, 'end_date')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            dateFormat="yyyy-MM-dd"
                            isClearable
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Renewal Date
                          </label>
                          <DatePicker
                            selected={formData.renewal_date}
                            onChange={(date) => handleDateChange(date, 'renewal_date')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            dateFormat="yyyy-MM-dd"
                            isClearable
                            placeholderText="Select renewal date"
                          />
                        </div>

                        {formData.status === 'cancelled' && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cancellation Date *
                              </label>
                              <DatePicker
                                selected={formData.cancellation_date}
                                onChange={(date) => handleDateChange(date, 'cancellation_date')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                dateFormat="yyyy-MM-dd"
                                required
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cancellation Reason
                              </label>
                              <textarea
                                name="cancellation_reason"
                                value={formData.cancellation_reason}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div>
                      
                      {/* Table layout matching Active Policies */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Policy #</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insurer</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Premium Collection Date</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Inception Date</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Renewal Date</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Premium</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr className="hover:bg-gray-50 border-b border-gray-100">
                              <td className="px-4 py-3 text-sm text-gray-800 font-medium">{policy.policy_number}</td>
                              <td className="px-4 py-3 text-sm text-gray-800">{policy.insurer || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-sm">
                                <div className="flex flex-col gap-2">
                                  <div>
                                    {policy.premium_collection_date ? (
                                      (() => {
                                        const date = new Date(policy.premium_collection_date);
                                        const day = date.getDate();
                                        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                                        if (day === lastDayOfMonth) {
                                          return <span className="text-gray-800">Last Day of Month</span>;
                                        }
                                        return <span className="text-gray-800">{day}</span>;
                                      })()
                                    ) : (
                                      <span className="text-gray-400 italic">Not set</span>
                                    )}
                                  </div>
                                  <div className="flex gap-3">
                                    <label className="flex items-center gap-1 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={formData.whatsapp_notification}
                                        disabled
                                        className="rounded"
                                      />
                                      <span className="text-gray-600">WhatsApp</span>
                                    </label>
                                    <label className="flex items-center gap-1 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={formData.email_notification}
                                        disabled
                                        className="rounded"
                                      />
                                      <span className="text-gray-600">Email</span>
                                    </label>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(policy.start_date)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {policy.renewal_date ? formatDate(policy.renewal_date) : ''}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-800">{formatCurrency(policy.premium_amount)}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  policy.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                              {/* Show cancellation details if policy is cancelled */}
                              {policy.status === 'cancelled' && policy.cancellation_date && (
                                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
                                  <h4 className="text-sm font-semibold text-red-800 mb-2">Cancellation Details</h4>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-red-600">Cancellation Date</label>
                                      <p className="text-sm text-red-700">{formatDate(policy.cancellation_date)}</p>
                                    </div>
                                    {policy.cancellation_reason && (
                                      <div className="col-span-2">
                                        <label className="block text-xs font-medium text-red-600">Cancellation Reason</label>
                                        <p className="text-sm text-red-700">{policy.cancellation_reason}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Cancel Policy Modal */}
                              {showCancelModal && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                                    <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                                      <h3 className="text-lg font-semibold">Cancel Policy</h3>
                                      <button
                                        onClick={() => setShowCancelModal(false)}
                                        className="text-gray-400 hover:text-gray-600 text-2xl"
                                      >
                                        ×
                                      </button>
                                    </div>

                                    <div className="p-6 space-y-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Termination Date *
                                        </label>
                                        <DatePicker
                                          selected={cancelData.termination_date}
                                          onChange={(date) => setCancelData(prev => ({ ...prev, termination_date: date || new Date() }))}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          dateFormat="yyyy-MM-dd"
                                          required
                                        />
                                      </div>

                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Cancellation Reason
                                        </label>
                                        <textarea
                                          value={cancelData.cancellation_reason}
                                          onChange={(e) => setCancelData(prev => ({ ...prev, cancellation_reason: e.target.value }))}
                                          rows={4}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          placeholder="Please provide a reason for cancelling this policy..."
                                        />
                                      </div>

                                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                        <p className="text-sm text-yellow-800">
                                          ⚠️ Warning: This action will change the policy status to "Cancelled" and cannot be undone.
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
                                      <button
                                        onClick={() => setShowCancelModal(false)}
                                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={handleCancelPolicy}
                                        disabled={!cancelData.termination_date}
                                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        Confirm Cancellation
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

              {activeTab === 'notes' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Policy Activity Log</h3>
                  <div className="space-y-3">
                    {policyNotes.length === 0 ? (
                      <p className="text-gray-500 italic">No activity recorded yet</p>
                    ) : (
                      policyNotes.map((note: any) => (
                        <div key={note.id} className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className={`inline-block px-2 py-1 text-xs font-medium rounded mr-2 ${
                                note.action_type === 'CREATED' ? 'bg-green-100 text-green-800' :
                                note.action_type === 'EDITED' ? 'bg-blue-100 text-blue-800' :
                                note.action_type === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                note.action_type === 'DELETED' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {note.action_type}
                              </span>
                              <p className="text-sm text-gray-700 mt-1">{note.description}</p>
                              {note.changes && note.action_type === 'EDITED' && (
                                <details className="mt-2 text-xs text-gray-500">
                                  <summary className="cursor-pointer">View changes</summary>
                                  <pre className="mt-1 p-2 bg-gray-100 rounded overflow-x-auto">
                                    {JSON.stringify(note.changes, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                            <div className="text-right text-xs text-gray-400">
                              <div>{new Date(note.created_at).toLocaleString()}</div>
                              <div>by {note.created_by}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'documents' && (
                <PolicyDocumentsTab policyId={parseInt(policyId!)} />
              )}

              {activeTab === 'whatsapp' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">WhatsApp Communication</h3>
                  <p className="text-gray-500">Placeholder for WhatsApp integration</p>
                </div>
              )}

              {activeTab === 'email' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Email Communication</h3>
                  <p className="text-gray-500">Placeholder for email integration</p>
                </div>
              )}
            </div>

            {/* PRODUCTS SECTION - Active Products only - Only show on Policy Details tab */}
{activeTab === 'policyDetails' && (
  <div className="mt-8 pt-6 border-t border-gray-200">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-2 h-2 rounded-full bg-green-500"></div>
      <h3 className="text-lg font-semibold text-gray-800">Active Products</h3>
      <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
        {clientProducts.filter((p: any) => p.status !== 'cancelled').length}
      </span>
    </div>

    <div className="space-y-6">

    {/* PROPERTY PRODUCTS - Only show if there are active property products */}
  {(() => {
    const activeProperties = clientProducts.filter((p: any) => p.product_type === 'properties' && p.status !== 'cancelled');
    return activeProperties.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">Property</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeProperties.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Address</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('properties')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {propertySortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortProductsByDate(activeProperties, propertySortOrder).map((product: any, idx: number) => (
                <tr key={`active-property-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product), { 
                        state: { 
                          existingPropertyData: product,
                          fromPolicy: true,
                          clientId: policy?.client_id,
                          quoteId: policy?.quote_id
                        } 
                      })}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {getPropertyAddress(product) || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}

          {/* HOUSEHOLD CONTENTS PRODUCTS - Only show if there are active household products */}
  {(() => {
    const activeHousehold = clientProducts.filter((p: any) => p.product_type === 'household-contents' && p.status !== 'cancelled');
    return activeHousehold.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">Household Contents</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeHousehold.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Address</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('household-contents')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {householdSortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortProductsByDate(activeHousehold, householdSortOrder).map((product: any, idx: number) => (
                <tr key={`active-household-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product))}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {getPropertyAddress(product) || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}

          {/* VEHICLE PRODUCTS - Only show if there are active vehicle products */}
  {(() => {
    const activeVehicles = clientProducts.filter((p: any) => p.product_type === 'vehicles' && p.status !== 'cancelled');
    return activeVehicles.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">Vehicles</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeVehicles.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Registration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Model</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Year Model</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('vehicles')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {vehicleSortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortProductsByDate(activeVehicles, vehicleSortOrder).map((product: any, idx: number) => (
                <tr key={`active-vehicle-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800 font-mono">
                    {product.registration_number || product.registrationNumber || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product))}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {`${product.make || ''} ${product.model || ''}`.trim() || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.year || product.model_year || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}

          {/* TRAILER & CARAVAN PRODUCTS - Only show if there are active trailer products */}
  {(() => {
    const activeTrailers = clientProducts.filter((p: any) => p.product_type === 'trailer-caravans' && p.status !== 'cancelled');
    return activeTrailers.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">Trailer & Caravan</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeTrailers.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Reg</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Model</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Year Model</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('trailer-caravans')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {trailerSortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortProductsByDate(activeTrailers, trailerSortOrder).map((product: any, idx: number) => (
                <tr key={`active-trailer-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800 font-mono">
                    {product.registration_number || product.registrationNumber || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product))}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {`${product.make || ''} ${product.model || ''}`.trim() || product.vehicle_type || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.year || product.model_year || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}

          {/* WATER CRAFT PRODUCTS - Only show if there are active water craft products */}
  {(() => {
    const activeWatercraft = clientProducts.filter((p: any) => p.product_type === 'water-craft' && p.status !== 'cancelled');
    return activeWatercraft.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">Water Craft</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeWatercraft.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Registration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Model</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('water-craft')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {watercraftSortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortProductsByDate(activeWatercraft, watercraftSortOrder).map((product: any, idx: number) => (
                <tr key={`active-watercraft-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800 font-mono">
                    {product.registration_number || product.registrationNumber || product.trailer_registration || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product))}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {product.vessel_name || product.name || `${product.make || ''} ${product.model || ''}`.trim() || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}

          {/* ALL RISK PRODUCTS - Only show if there are active risk items */}
  {(() => {
    const activeRiskItems = clientProducts.filter((p: any) => p.product_type === 'risk-items' && p.status !== 'cancelled');
    return activeRiskItems.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">All Risk</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeRiskItems.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Description</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('risk-items')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {riskItemSortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortProductsByDate(activeRiskItems, riskItemSortOrder).map((product: any, idx: number) => (
                <tr key={`active-allrisk-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product))}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {product.item_name || product.name || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}
          
          {/* VALUE ADDED PRODUCTS - Only show if there are active value added products */}
  {(() => {
    const activeValueAdded = clientProducts.filter((p: any) => p.product_type === 'value-added' && p.status !== 'cancelled');
    return activeValueAdded.length > 0 ? (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800 text-base">Value Added</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {activeValueAdded.length} Active
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Description</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleSort('value-added')}
                >
                  <div className="flex items-center gap-1">
                    Inception Date
                    {valueAddedSortOrder === 'desc' ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Insured Value</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeValueAdded.map((product: any, idx: number) => (
                <tr key={`active-valueadded-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button
                      onClick={() => navigate(getProductEditUrl(product))}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer"
                    >
                      {getProductDescription(product, product.product_type) || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {product.inception_date || policy?.start_date ? 
                      formatDate(product.inception_date || policy?.start_date) : 
                      '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(getProductValue(product))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleRemoveProduct(product)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
  })()}

    </div>

    {/* CANCELLED PRODUCTS SECTION */}
    {(() => {
      // Combine cancelled products from API and localStorage
      const apiCancelled = cancelledProducts || [];
      const cancelledKey = `policy_${policyId}_cancelled`;
      const localCancelled = JSON.parse(localStorage.getItem(cancelledKey) || '[]');
      const allCancelled = [...apiCancelled, ...localCancelled];
      
      return allCancelled.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-300">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <h3 className="text-lg font-semibold text-gray-800">Cancelled Products</h3>
            <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
              {allCancelled.length}
            </span>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full bg-white">
              <thead className="bg-red-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Product / Category</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Inception Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Termination Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Cancellation Reason</th>
                </tr>
              </thead>
              <tbody>
                {allCancelled.map((product: any, idx: number) => (
                  <tr key={`cancelled-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {getProductTypeDisplay(product.product_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {product.product_name || product.description || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {product.inception_date || policy?.start_date ? formatDate(product.inception_date || policy?.start_date) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(product.cancellation_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                      {product.cancellation_reason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    })()}

  </div>
)}

          </div>
        </div>
      </div>
      {/* Select Product Type Modal */}
{isAddProductModalVisible && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
    <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
      <h3 className="text-xl font-bold mb-4">Select Product Type</h3>
      
      <div className="space-y-3">
        

        {/* Property */}
<button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const returnUrl = `/policy/${policyId}`;
    setIsAddProductModalVisible(false);
    // ✅ Add policyId parameter
    navigate(`/add-property/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`);
  }}
  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
  Property
</button>

        {/* Household Contents */}
<button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const returnUrl = `/policy/${policyId}`;
    setIsAddProductModalVisible(false);
    // ✅ Add policyId parameter
    navigate(`/add-household-contents/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`);
  }}
  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
  Household Contents
</button>

        {/* Vehicle */}
<button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const returnUrl = `/policy/${policyId}`;
    setIsAddProductModalVisible(false);
    // ✅ Add policyId parameter
    navigate(`/add-vehicle/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`);
  }}
  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
  Vehicle
</button>

        {/* Trailer & Caravan */}
<button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const returnUrl = `/policy/${policyId}`;
    setIsAddProductModalVisible(false);
    // ✅ Add policyId parameter
    navigate(`/add-trailer-caravan/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`);
  }}
  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
  Trailer & Caravan
</button>

        {/* Value Added */}
          <button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const policyIdValue = policy?.id || parseInt(policyId as string);
    const returnUrl = `/policy/${policyId}`;
    
    console.log('🔍 Navigating to add value-added with:', {
      quoteId: currentQuoteId,
      clientId: clientIdValue,
      policyId: policyIdValue,
      returnUrl: returnUrl
    });
    
    setIsAddProductModalVisible(false);
    navigate(`/add-value-added/${currentQuoteId || 'null'}?clientId=${clientIdValue}&policyId=${policyIdValue}&fromPolicy=true&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
  Value Added
</button>

        {/* Water Craft */}
<button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const returnUrl = `/policy/${policyId}`;
    setIsAddProductModalVisible(false);
    // ✅ Add policyId parameter
    navigate(`/add-water-craft/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`);
  }}
  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
  </svg>
  Water Craft
</button>

        {/* Risk Items */}
<button
  onClick={() => {
    const currentQuoteId = policy?.quote_id;
    const clientIdValue = policy?.client_id;
    const returnUrl = `/policy/${policyId}`;
    setIsAddProductModalVisible(false);
    // ✅ Add policyId parameter
    navigate(`/add-risk-item/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}&fromPolicy=true&policyId=${policy?.id}`);
  }}
  className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
  Risk Items
</button>
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={() => setIsAddProductModalVisible(false)}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

      {/* Remove Product Modal */}
      {showRemoveProductModal && productToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Cancel Product</h3>
              <button
                onClick={() => setShowRemoveProductModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-700 mb-2">
                  You are about to cancel: <strong>{getProductName(productToRemove, productToRemove.product_type)}</strong>
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Type: {getProductTypeDisplay(productToRemove.product_type)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cancellation Reason *
                </label>
                <textarea
                  value={removalReason}
                  onChange={(e) => setRemovalReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Please provide a reason for cancelling this product..."
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Warning: This action will mark this product as "Cancelled" and move it to the Cancelled Products section.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowRemoveProductModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveProduct}
                disabled={!removalReason.trim() || removingProduct}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {removingProduct ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyDetail;